import { useState, Children, isValidElement } from 'react';
import type { FC, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLayout } from '../../context/LayoutContext';

interface Tab {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  children: (activeTab: string) => ReactNode;
  initialTab?: string;
}

export const Tabs: FC<TabsProps> = ({ tabs, children, initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || tabs[0].id);
  const { isNavVisible } = useLayout();

  const renderedChildren = children(activeTab);

  return (
    <div>
      {/* Desktop Tabs */}
      <div className="hidden sm:flex justify-center border-b border-gray-200">
        <div className="flex flex-wrap justify-center space-x-2 bg-gray-100 p-1 rounded-lg mb-6">
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center flex-1 py-3 px-4 sm:px-6 rounded-md text-sm font-semibold transition-all duration-300 focus:outline-none ${
                    activeTab === tab.id
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                    }`}
                >
                    {tab.icon && <span className="mr-2">{tab.icon}</span>}
                    {tab.label}
                </button>
            ))}
        </div>
      </div>

      {/* Page Content */}
      <div className="mt-6 sm:mt-0 sm:pb-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {renderedChildren}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Mobile Bottom Navigation */}
      <AnimatePresence>
        {isNavVisible && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: "0%" }}
            exit={{ y: "100%" }}
            transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            className="sm:hidden fixed bottom-0 left-0 right-0 h-20 bg-white/80 backdrop-blur-sm border-t border-gray-200 shadow-t-strong z-50"
          >
            <nav className="flex justify-around items-center h-full">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center justify-center gap-1 transition-colors duration-200 w-full h-full ${
                    activeTab === tab.id
                      ? 'text-primary'
                      : 'text-gray-500 hover:text-primary'
                  }`}
                >
                  {tab.icon}
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}; 